import { AddressSearchResponse } from '@application/components/address-search/AddressSearch';
import { ReportAggregation, SiteNetwork } from '@domain/graphql.types';
import { createContext } from 'react';
import { CombinedError } from 'urql';
import { defaultSearchFilters, FilterCategory, SearchFilters } from '../filters/searchFilters';
import { SiteLookupFilters } from '../filters/useSiteLookupFilters';
import type { SiteSearchOutcome, SiteSearchRequest, SiteSearchResult } from '../hooks/useSiteSearch';
import { SiteLookupDataView } from '../siteLookupDataView';

export type SelectedSite = {
  id: string;
  origin: 'list' | 'map';
};

export type SiteNetworksCatalogue = {
  data?: readonly SiteNetwork[];
  error?: CombinedError;
  fetching: boolean;
  retry: () => void;
};

export type SiteLookupContextProps = {
  /** filters of the displayed results, used by the search and the report */
  appliedFilters: SearchFilters;
  applyFilters: (filters: SearchFilters) => Promise<SiteSearchOutcome>;
  dataView: SiteLookupDataView;
  downloadReport: (aggregation: ReportAggregation, months: number) => void;
  failedRequest: SiteSearchRequest | null;
  filters: SiteLookupFilters;
  hasHistoricalAccess: boolean;
  isDownloadingSitesReport: boolean;
  hasRealTimeAccess: boolean;
  maxHistoricalMonths: number;
  networks: SiteNetworksCatalogue;
  /** the filter category whose popover is open */
  openFilter: FilterCategory | null;
  pendingRequest: SiteSearchRequest | null;
  recentSearches: AddressSearchResponse[];
  remainingDownloads: number;
  /** last successful search, kept while a new one is pending or after it failed */
  result: SiteSearchResult | null;
  retrySearch: () => void;
  setDataView: (view: SiteLookupDataView) => void;
  setOpenFilter: (category: FilterCategory | null) => void;
  updateFilterAddress: (address: AddressSearchResponse) => void;
  updateFilterRadius: (radius: number) => void;
};

export const defaultSiteLookupContext: SiteLookupContextProps = {
  appliedFilters: defaultSearchFilters,
  applyFilters: async () => 'success',
  dataView: 'historical',
  downloadReport: () => {},
  failedRequest: null,
  filters: { radius: 0 },
  hasHistoricalAccess: false,
  isDownloadingSitesReport: false,
  hasRealTimeAccess: false,
  maxHistoricalMonths: 3,
  networks: { fetching: false, retry: () => {} },
  openFilter: null,
  pendingRequest: null,
  recentSearches: [],
  remainingDownloads: 0,
  result: null,
  retrySearch: () => {},
  setDataView: () => {},
  setOpenFilter: () => {},
  updateFilterAddress: () => {},
  updateFilterRadius: () => {},
};

const SiteLookupContext = createContext<SiteLookupContextProps>(defaultSiteLookupContext);

export default SiteLookupContext;
